import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box, CircularProgress, IconButton, Skeleton, Typography } from "@mui/material";
import {
  cancelTwoFactorSetup,
  clearTwoFactorError,
  disableTwoFactor,
  enableTwoFactor,
  fetchTwoFactorStatus,
  regenerateBackupCodes,
  resetTwoFactor,
  setupTwoFactor,
} from "../../../../store/features/twoFactorApi";
import SettingsPrimaryButton from "../shared/SettingsPrimaryButton";
import SettingsSecondaryButton from "../shared/SettingsSecondaryButton";
import {
  COLORS,
  CONTENT_MAX_WIDTH,
  FONT,
  cardDescSx,
  separatorSx,
} from "../shared/styles";
import { PhoneIcon, SwitchOff, SwitchOn } from "./icons";
import BackupCodesSection from "./BackupCodesSection";
import SixDigitOtpInput from "../../../common/SixDigitOtpInput";

const SETUP_STEPS = [
  "Download an authenticator app",
  "Scan this QR code with your app",
  "Enter the 6-digit code to verify",
];

const featureIconSx = {
  width: 64,
  height: 64,
  borderRadius: "8px",
  border: `1px solid ${COLORS.border}`,
  background: COLORS.white,
  boxShadow: COLORS.shadow,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const featureTitleSx = {
  color: COLORS.textPrimary,
  fontFamily: FONT.family,
  fontSize: "18px",
  fontWeight: 500,
  lineHeight: "34px",
  m: 0,
};

function getErrorMessage(payload) {
  if (!payload) return "Something went wrong";
  if (typeof payload === "string") return payload;
  return (
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) && payload.errors[0]?.message) ||
    "Something went wrong"
  );
}

function TwoFactorSection() {
  const dispatch = useDispatch();
  const {
    enabled,
    pendingSetup,
    statusLoading,
    setupLoading,
    enableLoading,
    disableLoading,
    regenerateLoading,
    qrCodeDataUrl,
    backupCodes,
    error,
    statusLoaded,
  } = useSelector((state) => state.twoFactor);

  const [verificationCode, setVerificationCode] = useState("");
  const [codeError, setCodeError] = useState("");

  const showSetup = setupLoading || pendingSetup || Boolean(qrCodeDataUrl);
  const toggleBusy = setupLoading || enableLoading || disableLoading || statusLoading;

  useEffect(() => {
    dispatch(fetchTwoFactorStatus());
    return () => {
      dispatch(resetTwoFactor());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!statusLoaded) return;
    if (pendingSetup && !qrCodeDataUrl && !setupLoading) {
      dispatch(setupTwoFactor());
    }
  }, [statusLoaded, pendingSetup, qrCodeDataUrl, setupLoading, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(getErrorMessage(error));
      dispatch(clearTwoFactorError());
    }
  }, [error, dispatch]);

  const handleToggle = async () => {
    if (toggleBusy) return;

    if (enabled) {
      const action = await dispatch(disableTwoFactor());
      if (disableTwoFactor.fulfilled.match(action)) {
        setVerificationCode("");
        toast.success(action.payload?.message || "Two-factor authentication disabled.");
      }
      return;
    }

    const action = await dispatch(setupTwoFactor());
    if (setupTwoFactor.fulfilled.match(action)) {
      setVerificationCode("");
      setCodeError("");
    }
  };

  const handleCancelSetup = async () => {
    const action = await dispatch(cancelTwoFactorSetup());
    if (cancelTwoFactorSetup.fulfilled.match(action)) {
      setVerificationCode("");
      setCodeError("");
      toast.success("Two-factor setup cancelled.");
    }
  };

  const handleEnable = async () => {
    const code = verificationCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setCodeError("Enter a valid 6-digit code.");
      return;
    }

    setCodeError("");
    const action = await dispatch(enableTwoFactor({ code }));
    if (enableTwoFactor.fulfilled.match(action)) {
      setVerificationCode("");
      toast.success(action.payload?.message || "Two-factor authentication enabled.");
    }
  };

  const handleRegenerate = async () => {
    const action = await dispatch(regenerateBackupCodes());
    if (regenerateBackupCodes.fulfilled.match(action)) {
      toast.success(action.payload?.message || "Backup codes regenerated.");
    }
  };

  const handleDownloadBackupCodes = () => {
    if (!backupCodes.length) return;
    const content = backupCodes.join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "trustchat-backup-codes.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <Box sx={featureIconSx}>
          <PhoneIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="p" sx={featureTitleSx}>
            Two-Factor Authentication
          </Typography>
          <Typography component="p" sx={cardDescSx}>
            Add an extra layer of security to your account
          </Typography>
        </Box>
        <IconButton
          type="button"
          onClick={handleToggle}
          disabled={toggleBusy || showSetup}
          aria-label={enabled ? "Disable 2FA" : "Enable 2FA"}
          disableRipple
          sx={{
            p: 0,
            flexShrink: 0,
            opacity: toggleBusy || showSetup ? 0.6 : 1,
            "&:hover": { background: "none" },
          }}
        >
          {toggleBusy ? (
            <CircularProgress size={24} />
          ) : enabled ? (
            <SwitchOn />
          ) : (
            <SwitchOff />
          )}
        </IconButton>
      </Box>

      {showSetup && !enabled ? (
        <Box sx={{ mt: "32px", width: "100%", maxWidth: CONTENT_MAX_WIDTH }}>
          <Box
            sx={{
              display: "flex",
              gap: "32px",
              alignItems: "flex-start",
              "@media (max-width: 768px)": {
                flexDirection: "column",
              },
            }}
          >
            <Box
              sx={{
                width: 224,
                height: 224,
                borderRadius: "8px",
                border: `1px solid ${COLORS.border}`,
                background: COLORS.white,
                boxShadow: COLORS.shadow,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {setupLoading && !qrCodeDataUrl ? (
                <Skeleton
                  variant="rounded"
                  width={200}
                  height={200}
                  sx={{ borderRadius: "4px" }}
                />
              ) : qrCodeDataUrl ? (
                <Box
                  component="img"
                  src={qrCodeDataUrl}
                  alt="Authenticator QR code"
                  sx={{ width: 200, height: 200, objectFit: "contain" }}
                />
              ) : null}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component="h3"
                sx={{
                  color: COLORS.textPrimary,
                  fontFamily: FONT.family,
                  fontSize: "20px",
                  fontWeight: 500,
                  lineHeight: "34px",
                  m: "0 0 16px",
                }}
              >
                Setup Instructions
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {SETUP_STEPS.map((step, index) => (
                  <Box
                    component="li"
                    key={step}
                    sx={{ display: "flex", alignItems: "center", gap: "16px" }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: "#F3FBFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          color: COLORS.primary,
                          fontFamily: FONT.family,
                          fontSize: "14px",
                          fontWeight: 500,
                          lineHeight: "24px",
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </Box>
                    <Typography
                      component="span"
                      sx={{
                        color: COLORS.textSecondary,
                        fontFamily: FONT.family,
                        fontSize: "16px",
                        fontWeight: 400,
                        lineHeight: "28px",
                      }}
                    >
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: "24px" }}>
                <Typography
                  sx={{
                    color: COLORS.textSecondary,
                    fontFamily: FONT.family,
                    fontSize: "12px",
                    mb: "8px",
                  }}
                >
                  6-digit verification code
                </Typography>
                <SixDigitOtpInput
                  value={verificationCode}
                  onChange={(value) => {
                    setVerificationCode(value);
                    if (codeError) setCodeError("");
                  }}
                  length={6}
                  autoFocus
                  disabled={enableLoading || setupLoading}
                />
                {codeError ? (
                  <Typography
                    sx={{
                      mt: 1,
                      color: "#DC2626",
                      fontFamily: FONT.family,
                      fontSize: "12px",
                    }}
                  >
                    {codeError}
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ mt: "24px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <SettingsPrimaryButton
                  type="button"
                  onClick={handleEnable}
                  disabled={enableLoading || setupLoading || verificationCode.length !== 6}
                  sx={{ minWidth: 140 }}
                >
                  {enableLoading ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : (
                    "Verify & Enable"
                  )}
                </SettingsPrimaryButton>
                <SettingsSecondaryButton
                  type="button"
                  onClick={handleCancelSetup}
                  disabled={setupLoading || enableLoading}
                  sx={{ width: 140 }}
                >
                  Cancel
                </SettingsSecondaryButton>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}

      {enabled ? (
        <>
          <Box sx={{ ...separatorSx, mt: "32px" }} />
          <BackupCodesSection
            codes={backupCodes}
            onDownload={handleDownloadBackupCodes}
            onRegenerate={handleRegenerate}
            regenerateLoading={regenerateLoading}
          />
        </>
      ) : null}
    </>
  );
}

export default TwoFactorSection;
